import { Badge } from "@/components/ui/badge";
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  getBillingSummary,
  getStatusBreakdown,
  getPlanBreakdown,
  getMonthlyEvolution,
  getExpiringTrials,
  getPastDueTenants,
} from "@/lib/db/queries/billing";
import { logActivity } from "@/lib/db/audit";
import { getSession } from "@/lib/auth/session";

export const dynamic = "force-dynamic";

const numFmt = new Intl.NumberFormat("fr-FR");
const priceFmt = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});
const dateFmt = new Intl.DateTimeFormat("fr-FR", {
  day: "2-digit",
  month: "short",
  year: "numeric",
});
const monthFmt = new Intl.DateTimeFormat("fr-FR", {
  month: "short",
  year: "2-digit",
});

export default async function BillingPage() {
  const session = await getSession();
  let summary: Awaited<ReturnType<typeof getBillingSummary>> | null = null;
  let statusBd: Awaited<ReturnType<typeof getStatusBreakdown>> | null = null;
  let planBd: Awaited<ReturnType<typeof getPlanBreakdown>> = [];
  let monthly: Awaited<ReturnType<typeof getMonthlyEvolution>> = [];
  let expiringTrials: Awaited<ReturnType<typeof getExpiringTrials>> = [];
  let pastDue: Awaited<ReturnType<typeof getPastDueTenants>> = [];
  let loadError: string | null = null;
  try {
    [summary, statusBd, planBd, monthly, expiringTrials, pastDue] =
      await Promise.all([
        getBillingSummary(),
        getStatusBreakdown(),
        getPlanBreakdown(),
        getMonthlyEvolution(),
        getExpiringTrials(),
        getPastDueTenants(),
      ]);
    void logActivity({
      type: "superadmin.billing_view",
      title: "Vue suivi paiements / abonnements",
      user_id: session?.user.id,
    });
  } catch (err) {
    loadError = err instanceof Error ? err.message : "Erreur inconnue";
  }

  const maxMonthlyValue = Math.max(
    1,
    ...monthly.flatMap((m) => [m.signups, m.conversions_to_paid, m.cancellations]),
  );

  return (
    <div className="px-6 py-6 lg:px-10">
      <header className="mb-6">
        <h1 className="text-2xl font-semibold">Abonnements & paiements</h1>
        <p className="text-sm text-muted-foreground">
          Suivi du chiffre d&apos;affaires récurrent, des conversions trial → payant
          et des paiements en échec.
        </p>
      </header>

      {loadError && (
        <div className="rounded-md border border-red-200 bg-danger-subtle px-4 py-3 text-sm text-danger">
          Impossible de charger les statistiques : {loadError}
        </div>
      )}

      {!loadError && summary && statusBd && (
        <>
          {/* KPIs principaux */}
          <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <KpiTile
              label="MRR"
              value={priceFmt.format(Number(summary.mrr_eur))}
              hint="Revenu récurrent mensuel (subs actives)"
              tone="success"
            />
            <KpiTile
              label="ARR"
              value={priceFmt.format(Number(summary.arr_eur))}
              hint="Annual Recurring Revenue"
              tone="success"
            />
            <KpiTile
              label="Inscriptions (30 j)"
              value={numFmt.format(summary.signups_30j)}
              hint={`${summary.total_tenants} tenants au total`}
              tone="accent"
            />
            <KpiTile
              label="Paiements en échec"
              value={numFmt.format(summary.past_due)}
              hint={
                summary.past_due > 0
                  ? "À traiter en priorité"
                  : "Aucun past-due"
              }
              tone={summary.past_due > 0 ? "danger" : "success"}
            />
          </section>

          {/* Répartition par statut */}
          <section className="mb-6">
            <h2 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Répartition des abonnements
            </h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatusTile
                label="En essai"
                count={statusBd.trialing}
                tone="warning"
              />
              <StatusTile
                label="Actifs payants"
                count={statusBd.active}
                tone="success"
              />
              <StatusTile
                label="Paiement en échec"
                count={statusBd.past_due}
                tone="danger"
              />
              <StatusTile
                label="Annulés"
                count={statusBd.canceled}
                tone="neutral"
              />
            </div>
          </section>

          {/* Détail par plan */}
          <section className="mb-6">
            <h2 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
              Détail par plan
            </h2>
            <div className="overflow-hidden rounded-lg border border-border bg-surface">
              <table className="w-full text-sm">
                <thead className="bg-surface-2 text-left text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5">Plan</th>
                    <th className="px-4 py-2.5 text-right">Tarif mens.</th>
                    <th className="px-4 py-2.5 text-right">Tarif annuel</th>
                    <th className="px-4 py-2.5 text-right">Abonnés actifs</th>
                    <th className="px-4 py-2.5 text-right">Mensuel / Annuel</th>
                    <th className="px-4 py-2.5 text-right">MRR</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {planBd.map((p) => (
                    <tr key={p.plan_id}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium">{p.plan_name}</div>
                        <div className="font-mono text-[10px] uppercase text-muted-foreground">
                          {p.plan_code}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs tabular-nums">
                        {priceFmt.format(Number(p.monthly_price))}
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs tabular-nums">
                        {priceFmt.format(Number(p.yearly_price))}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums">
                        {numFmt.format(p.active_count)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-xs tabular-nums">
                        {p.monthly_count} / {p.yearly_count}
                      </td>
                      <td className="px-4 py-2.5 text-right font-semibold text-success tabular-nums">
                        {priceFmt.format(Number(p.mrr_contribution))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* Évolution mensuelle */}
          {monthly.length > 0 && (
            <section className="mb-6">
              <h2 className="mb-2 text-sm font-medium uppercase tracking-wider text-muted-foreground">
                Évolution sur 12 mois
              </h2>
              <div className="overflow-hidden rounded-lg border border-border bg-surface p-4">
                <div className="mb-3 flex items-center gap-4 text-[11px] text-muted-foreground">
                  <LegendItem color="bg-accent" label="Inscriptions" />
                  <LegendItem color="bg-success" label="Conversions payantes" />
                  <LegendItem color="bg-danger" label="Annulations" />
                </div>
                <div className="grid grid-cols-12 gap-1.5">
                  {monthly.map((m) => (
                    <MonthBar
                      key={m.month}
                      month={m.month}
                      signups={m.signups}
                      conversions={m.conversions_to_paid}
                      cancellations={m.cancellations}
                      maxValue={maxMonthlyValue}
                    />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Trials qui se terminent + paiements en échec */}
          <section className="grid gap-4 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>Essais qui se terminent</CardTitle>
                    <CardDescription>
                      14 prochains jours — relancer pour la conversion.
                    </CardDescription>
                  </div>
                  <Badge tone="warning">{expiringTrials.length}</Badge>
                </div>
              </CardHeader>
              <CardBody>
                {expiringTrials.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    Aucun essai en passe d&apos;expirer.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {expiringTrials.map((t) => (
                      <li
                        key={t.tenant_id}
                        className="flex items-center gap-3 py-2 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">
                            {t.tenant_name}
                          </div>
                          <div className="font-mono text-[10px] text-muted-foreground">
                            {t.tenant_slug} · {t.plan_name ?? "—"}
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs tabular-nums">
                            {dateFmt.format(new Date(t.trial_end))}
                          </div>
                          <Badge
                            tone={t.days_left <= 3 ? "danger" : "warning"}
                          >
                            {t.days_left >= 0
                              ? `${t.days_left} j`
                              : `expiré (${-t.days_left} j)`}
                          </Badge>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle>Paiements en échec</CardTitle>
                    <CardDescription>
                      Subscriptions past_due — à recouvrer ou suspendre.
                    </CardDescription>
                  </div>
                  <Badge tone={pastDue.length > 0 ? "danger" : "success"}>
                    {pastDue.length}
                  </Badge>
                </div>
              </CardHeader>
              <CardBody>
                {pastDue.length === 0 ? (
                  <p className="text-sm text-success">
                    ✓ Tous les prélèvements sont à jour.
                  </p>
                ) : (
                  <ul className="divide-y divide-border">
                    {pastDue.map((t) => (
                      <li
                        key={t.tenant_id}
                        className="flex items-center gap-3 py-2 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium truncate">
                            {t.tenant_name}
                          </div>
                          <div className="font-mono text-[10px] text-muted-foreground">
                            {t.tenant_slug} · {t.plan_name ?? "—"}
                          </div>
                        </div>
                        <div className="text-right">
                          {t.monthly_price && (
                            <div className="text-xs text-danger font-medium tabular-nums">
                              {priceFmt.format(Number(t.monthly_price))} dû
                            </div>
                          )}
                          {t.current_period_end && (
                            <div className="text-[10px] text-muted-foreground tabular-nums">
                              période fin{" "}
                              {dateFmt.format(new Date(t.current_period_end))}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardBody>
            </Card>
          </section>
        </>
      )}
    </div>
  );
}

function KpiTile({
  label,
  value,
  hint,
  tone = "neutral",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "neutral" | "accent" | "success" | "warning" | "danger";
}) {
  const toneCls = {
    neutral: "text-foreground",
    accent: "text-accent",
    success: "text-success",
    warning: "text-warning",
    danger: "text-danger",
  }[tone];
  return (
    <div className="rounded-lg border border-border bg-surface p-4">
      <p className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        {label}
      </p>
      <p className={`mt-1 text-2xl font-semibold tabular-nums ${toneCls}`}>
        {value}
      </p>
      {hint && (
        <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>
      )}
    </div>
  );
}

function StatusTile({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: "warning" | "success" | "danger" | "neutral";
}) {
  const toneCls = {
    warning: "text-warning bg-warning-subtle",
    success: "text-success bg-success-subtle",
    danger: "text-danger bg-danger-subtle",
    neutral: "text-muted-foreground bg-surface-2",
  }[tone];
  return (
    <div className={`rounded-lg p-4 ${toneCls}`}>
      <p className="text-[10px] font-medium uppercase tracking-wider opacity-80">
        {label}
      </p>
      <p className="mt-1 text-2xl font-semibold tabular-nums">
        {numFmt.format(count)}
      </p>
    </div>
  );
}

function MonthBar({
  month,
  signups,
  conversions,
  cancellations,
  maxValue,
}: {
  month: string;
  signups: number;
  conversions: number;
  cancellations: number;
  maxValue: number;
}) {
  const monthDate = new Date(`${month}-01T00:00:00`);
  const label = monthFmt.format(monthDate);
  const barH = (n: number) => Math.max(2, Math.round((n / maxValue) * 80));
  return (
    <div className="flex flex-col items-center gap-1 text-[10px] text-muted-foreground">
      <div className="flex h-20 w-full items-end justify-center gap-0.5">
        <div
          className="w-full max-w-[8px] rounded-sm bg-accent"
          style={{ height: `${barH(signups)}px` }}
          title={`${signups} inscription(s)`}
        />
        <div
          className="w-full max-w-[8px] rounded-sm bg-success"
          style={{ height: `${barH(conversions)}px` }}
          title={`${conversions} conversion(s)`}
        />
        <div
          className="w-full max-w-[8px] rounded-sm bg-danger"
          style={{ height: `${barH(cancellations)}px` }}
          title={`${cancellations} annulation(s)`}
        />
      </div>
      <div className="text-center text-[10px] tabular-nums">{label}</div>
    </div>
  );
}

function LegendItem({ color, label }: { color: string; label: string }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`inline-block h-2 w-2 rounded-sm ${color}`} />
      <span>{label}</span>
    </span>
  );
}
